import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { EMarket } from 'legacy/lut/configs/geo'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

const MARKET_OFF_SUFFIX = ':OFF'

const MARKET_CODES: Record<EMarket, string> = {
  [EMarket.latinAmerica]: 'LA',
  [EMarket.northAmerica]: 'NA',
  [EMarket.greaterAsia]: 'GA',
  [EMarket.greaterChina]: 'GC',
  [EMarket.india]: 'IBSL',
  [EMarket.emea]: 'EMEA',
}

const PROGRAM_TYPE_OFFERINGS: Partial<Record<ProgramTypes, string[]>> = {
  [ProgramTypes.PersonalSystems]: [ProgramTypes.PersonalSystems, ProgramTypes.PC],
  [ProgramTypes.PC]: [ProgramTypes.PC, ProgramTypes.PersonalSystems],
  [ProgramTypes.CorePrint]: [ProgramTypes.CorePrint, ProgramTypes.Print],
  [ProgramTypes.Print]: [ProgramTypes.Print, ProgramTypes.CorePrint],
  [ProgramTypes.LargeFormat]: [ProgramTypes.LargeFormat, ProgramTypes.LF],
  [ProgramTypes.LF]: [ProgramTypes.LF, ProgramTypes.LargeFormat],
}

const normalize = (value: string): string => value.trim().toUpperCase()

const normalizeBusinessPriorityLookupValue = (value: string): string => value.trim().toLowerCase()

const getEnabledMarketCode = (market: string): string | null => {
  const normalizedMarket = normalize(market)

  return normalizedMarket.endsWith(MARKET_OFF_SUFFIX) ? null : normalizedMarket
}

const getMarketAliases = (marketCode: string): string[] => {
  return marketCode === 'IBSL' ? ['IBSL', 'IB'] : [marketCode]
}

export const getBusinessPriorityMarketCode = (market?: EMarket | string | null): string | null => {
  if (!market) {
    return null
  }

  return MARKET_CODES[market as EMarket] ?? market
}

export const filterBusinessPriorities = ({
  priorities,
  fYear,
  programType,
  market,
}: {
  priorities: IKIBusinessPriority[]
  fYear: number
  programType: ProgramTypes
  market?: EMarket | string | null
}): IKIBusinessPriority[] => {
  const marketCode = getBusinessPriorityMarketCode(market)
  const marketAliases = marketCode ? getMarketAliases(normalize(marketCode)) : []
  const offeringAliases = (PROGRAM_TYPE_OFFERINGS[programType] ?? [programType]).map(normalize)
  const seenUids = new Set<string>()

  return priorities.filter((priority) => {
    const normalizedStatus = priority.status?.trim().toLowerCase()
    const isActive = !normalizedStatus || normalizedStatus === 'active'
    // TODO: `Enabled` is not present in KIBusinessPriorities in every environment yet.
    // Once it becomes mandatory in the backend contract, change this to `priority.enabled === true`.
    const isEnabled = priority.enabled !== false
    const isFYearMatch = priority.fYear === fYear
    const isOfferingMatch = priority.offerings.some((offering) =>
      offeringAliases.includes(normalize(offering))
    )
    const isMarketMatch = priority.markets.some((priorityMarket) => {
      const enabledMarketCode = getEnabledMarketCode(priorityMarket)

      return (
        enabledMarketCode === 'WW' ||
        (enabledMarketCode !== null && marketAliases.includes(enabledMarketCode))
      )
    })
    const normalizedPriorityUid = normalizeBusinessPriorityLookupValue(priority.uid)
    const isDuplicate = seenUids.has(normalizedPriorityUid)

    if (isActive && isEnabled && isFYearMatch && isOfferingMatch && isMarketMatch && !isDuplicate) {
      seenUids.add(normalizedPriorityUid)
      return true
    }

    return false
  })
}
